// Automatic emails: when someone gets a tag, they get a set of emails on a schedule.

import { useState } from 'preact/hooks';
import { api, ApiError } from '../lib/api';
import { navigate } from '../lib/router';
import { Button, Field, LoadError, Modal, Notice, Spinner, Toggle, confirmBox, toast, toastError, useLoad } from '../lib/ui';
import { Icon } from '../lib/icons';
import { num, plural } from '../lib/format';
import type { EmailSummary } from '../../shared/types';
import { WELCOME_TAG } from '../../shared/plan';
import { welcomeSteps } from './Year';

interface Step {
  email_id: number;
  delay_days: number;
  name: string;
  subject: string;
  status: string;
  sent: number;
  waiting: number;
}

interface Seq {
  id: number;
  name: string;
  trigger_tag: string;
  active: boolean;
  enrolled: number;
  steps: Step[];
}

export function Sequences() {
  const seqs = useLoad(() => api<{ sequences: Seq[] }>('/api/sequences').then((r) => r.sequences), []);
  const emails = useLoad(() => api<{ emails: EmailSummary[] }>('/api/emails').then((r) => r.emails), []);
  const tags = useLoad(() => api<{ tags: { tag: string; total: number }[] }>('/api/tags').then((r) => r.tags), []);
  const [creating, setCreating] = useState(false);
  const [welcoming, setWelcoming] = useState(false);

  const addWelcome = async () => {
    setWelcoming(true);
    try {
      await api('/api/sequences/welcome', { method: 'POST' });
      toast('Added. Test each email, then turn it on.');
      reload();
    } catch (e) {
      toastError(e);
    } finally {
      setWelcoming(false);
    }
  };

  const reload = () => {
    seqs.reload();
    emails.reload();
  };

  return (
    <div class="wrap">
      <div class="page-head">
        <div>
          <h1 class="display">Automatic emails</h1>
          <p class="lead">Emails that go out on their own.</p>
        </div>
        {!!seqs.data?.length && (
          <div class="head-actions">
            <Button kind="primary" icon="plus" onClick={() => setCreating(true)}>
              New automatic email
            </Button>
          </div>
        )}
      </div>

      {seqs.error ? (
        <LoadError error={seqs.error} retry={seqs.reload} />
      ) : !seqs.data ? (
        <Spinner />
      ) : seqs.data.length === 0 ? (
        <section class="card welcome-start">
          <h2 class="card-title">A hello for every new client</h2>
          <p class="muted">Nothing sends until you turn it on.</p>
          <ol class="welcome-start-steps">
            {welcomeSteps().map((st) => (
              <li key={st.key}>
                <span class="welcome-start-pic">{st.photo && <img src={st.photo} alt="" loading="lazy" />}</span>
                <small>{st.when}</small>
                <b>{st.subject}</b>
              </li>
            ))}
          </ol>
          <Button kind="primary" size="l" icon="plus" busy={welcoming} onClick={addWelcome}>
            Add the welcome series
          </Button>
        </section>
      ) : (
        <div class="seq-list">
          {seqs.data.map((s) => (
            <SeqCard key={s.id} s={s} drafts={(emails.data || []).filter((e) => e.status === 'draft' || s.steps.some((st) => st.email_id === e.id))} onChanged={reload} />
          ))}
        </div>
      )}

      {creating && (
        <CreateSeq
          tags={tags.data?.map((t) => t.tag) || []}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            seqs.reload();
          }}
        />
      )}
    </div>
  );
}

function SeqCard({ s, drafts, onChanged }: { s: Seq; drafts: EmailSummary[]; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ text: string; emailId?: number } | null>(null);

  const toggle = async (on: boolean) => {
    if (!on) {
      const ok = await confirmBox({
        title: 'Turn off?',
        body: "Nobody new gets these emails while it's off. People already waiting get theirs when you turn it back on.",
        ok: 'Turn off',
      });
      if (!ok) return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await api(`/api/sequences/${s.id}/${on ? 'activate' : 'pause'}`, { method: 'POST' });
      toast(on ? (s.trigger_tag === WELCOME_TAG ? 'On. New clients get these emails.' : `On. Anyone tagged "${s.trigger_tag}" gets these emails.`) : 'Turned off.');
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.data && typeof e.data === 'object') {
        const d = e.data as { error?: string; emailId?: number };
        setProblem({ text: d.error || e.message, emailId: d.emailId });
      } else toastError(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await confirmBox({ title: `Delete "${s.name}"?`, body: 'Emails still waiting to go out are canceled. The emails themselves stay in your list as drafts.', ok: 'Delete', danger: true });
    if (!ok) return;
    try {
      await api(`/api/sequences/${s.id}`, { method: 'DELETE' });
      toast('Deleted.');
      onChanged();
    } catch (e) {
      toastError(e);
    }
  };

  return (
    <section class={`card seq ${s.active ? 'seq-on' : ''}`}>
      <div class="seq-head">
        <div class="grow">
          <h2 class="card-title">{s.name}</h2>
          <p class="muted small">
            {s.trigger_tag === WELCOME_TAG ? (
              'For new clients'
            ) : (
              <>
                When someone is tagged <span class="tag">{s.trigger_tag}</span>
              </>
            )}{' '}
            · {plural(s.enrolled, 'person', 'people')} so far
          </p>
        </div>
        <span class={`seq-state ${s.active ? 'on' : ''}`}>{s.active ? 'On' : 'Off'}</span>
        <Toggle on={s.active} label={s.active ? 'Turn off' : 'Turn on'} onChange={(v) => !busy && toggle(v)} />
      </div>

      {problem && (
        <Notice
          kind="bad"
          action={
            problem.emailId ? (
              <Button size="s" onClick={() => navigate(`/emails/${problem.emailId}/edit`)}>
                Fix it
              </Button>
            ) : undefined
          }
        >
          {problem.text}
        </Notice>
      )}

      {editing ? (
        <StepEditor
          s={s}
          drafts={drafts}
          onDone={() => {
            setEditing(false);
            onChanged();
          }}
        />
      ) : s.steps.length === 0 ? (
        <div class="seq-empty">
          <p class="muted">No emails in it yet.</p>
          <Button icon="plus" onClick={() => setEditing(true)}>
            Add emails
          </Button>
        </div>
      ) : (
        <ol class="seq-steps">
          {s.steps.map((st, i) => (
            <li key={st.email_id}>
              <span class="seq-when">{st.delay_days === 0 ? 'Right away' : `${plural(st.delay_days, 'day')} later`}</span>
              <span class="seq-dot">{i + 1}</span>
              <span class="seq-email">
                <b>{st.name}</b>
                <small>{st.subject}</small>
              </span>
              <span class="seq-nums muted small">
                {num(st.sent)} sent{st.waiting ? ` · ${num(st.waiting)} waiting` : ''}
              </span>
            </li>
          ))}
        </ol>
      )}

      {!editing && (
        <div class="card-foot">
          {!s.active && s.steps.length > 0 && (
            <Button size="s" icon="edit" onClick={() => setEditing(true)}>
              Change emails
            </Button>
          )}
          {s.active && <span class="muted small">Turn it off to change the emails.</span>}
          <Button size="s" kind="quiet" class="danger-text" onClick={remove}>
            Delete
          </Button>
        </div>
      )}
    </section>
  );
}

function StepEditor({ s, drafts, onDone }: { s: Seq; drafts: EmailSummary[]; onDone: () => void }) {
  const [steps, setSteps] = useState(s.steps.map((st) => ({ email_id: st.email_id, delay_days: st.delay_days })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nameOf = (id: number) => drafts.find((d) => d.id === id)?.name || `Email ${id}`;
  const free = drafts.filter((d) => !steps.some((st) => st.email_id === d.id) && (!d.sequence || s.steps.some((st) => st.email_id === d.id)));

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/sequences/${s.id}`, { method: 'PUT', body: { steps } });
      toast("Saved. Turn it on when you're ready.");
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="step-editor">
      {steps.map((st, i) => (
        <div class="step-row" key={st.email_id}>
          <span class="seq-dot">{i + 1}</span>
          <b class="grow">{nameOf(st.email_id)}</b>
          <label class="delay">
            <input
              type="number"
              min={0}
              max={365}
              value={st.delay_days}
              onInput={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, delay_days: Math.max(0, Number(e.currentTarget.value) || 0) } : x)))}
            />
            <span>days after the tag</span>
          </label>
          <button class="icon-btn" aria-label="Remove" onClick={() => setSteps(steps.filter((_, j) => j !== i))}>
            <Icon name="x" size={16} />
          </button>
        </div>
      ))}
      {steps.length < 10 && (
        <div class="step-add">
          {free.length === 0 ? (
            <p class="muted small">
              Write a draft email first, then add it here.{' '}
              <button class="link" onClick={() => navigate('/?new=1')}>
                New email
              </button>
            </p>
          ) : (
            <select
              class="select"
              value=""
              aria-label="Add an email"
              onChange={(e) => {
                const id = Number(e.currentTarget.value);
                if (!id) return;
                const last = steps[steps.length - 1]?.delay_days ?? -7;
                setSteps([...steps, { email_id: id, delay_days: steps.length ? last + 7 : 0 }]);
              }}
            >
              <option value="">+ Add a draft email…</option>
              {free.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      {err && <Notice kind="bad">{err}</Notice>}
      <div class="card-foot">
        <Button kind="primary" busy={busy} onClick={save}>
          Save
        </Button>
        <Button kind="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function CreateSeq({ tags, onClose, onCreated }: { tags: string[]; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [tag, setTag] = useState(tags.includes('new-client') ? 'new-client' : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/api/sequences', { body: { name: name.trim() || undefined, trigger_tag: tag } });
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="New automatic email"
      onClose={onClose}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" busy={busy} disabled={!tag.trim()} onClick={create}>
            Create
          </Button>
        </>
      }
    >
      <div class="form-grid">
        <Field label="Name" hint="Just for you. For example: Welcome series">
          <input value={name} onInput={(e) => setName(e.currentTarget.value)} placeholder="Welcome series" />
        </Field>
        <Field label="Starts when someone is tagged" error={err} hint="Tag people on import, or on their contact page.">
          <input list="seq-tags" value={tag} onInput={(e) => setTag(e.currentTarget.value)} placeholder="new-client" />
        </Field>
        <datalist id="seq-tags">
          {tags.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </div>
    </Modal>
  );
}
